import { Injectable, inject } from '@angular/core';
import { NativeIds, NativeService } from './native.service';
import { EventManager } from '@shared/utils/event-manager';
import { LoggerService } from '@shared/services/logger.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuidv4 } from 'uuid';
import { DeviceDetectorService } from 'ngx-device-detector';
import { lastValueFrom } from 'rxjs';
import { GlobalDataRepository } from '../state/global-data/global-data.repository';

type DeviceIdType =
  // Generated from the client device
  | 'clientId'
  // Unique ID that Apple assigns to iOS devices
  | 'idfa'
  // Unique ID that AppsFlyer assigns to the user's device
  | 'appsFlyerId'
  // Unique ID that Firebase assigns to the user's device
  | 'firebaseAppInstanceId'
  // ID device for Playspace database
  | 'externalId';

type DeviceEnvironment = 'IOS' | 'GOOGLE_PLAY' | 'WEB' | 'AMAZON' | 'HUAWEI';
type DeviceType = 'ANDROID' | 'IOS' | 'DESKTOP_WEB' | 'MOBILE_WEB';
type DeviceErrorType =
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'UNKNOWN'
  | 'INVALID_ARGUMENTS'
  | 'PAYMENT_REPEATED_TRANSACTION'
  | 'PAYMENT_REPEATED'
  | 'PAYMENT_INVALID'
  | 'APPLE_PAYMENT_EXCEPTION';

interface FindDeviceRequestDto {
  deviceIds: {
    [deviceIdType in DeviceIdType]?: string;
  };
  info: string;
}

/// Data model of the Device Id request response
export interface DeviceDto {
  /// Country of the device
  country: string;
  /// Specifies what kind of device is
  deviceType: DeviceType;
  /// Specifies in which platform is the build deployed
  environment: DeviceEnvironment;
  /// Group of identifiers associated with the device
  gameIdentifiers: GameIdentifierDto[];
  /// Stored version of the application
  gameVersion: string;
  /// Global Space Go ID
  globalClientId: string;
  /// Unique value that identifies the device
  id: string;
  /// Unique ID that Apple assigns to iOS devices
  idfa: string;
  /// Last update of the device
  lastUpdate: string;
  /// Defines the user's language and variants
  locale: string;
}

interface GameIdentifierDto {
  /// Category of the identifier
  deviceIdentifierType: DeviceIdType;
  /// Value that identifies the application
  gameName: string;
  /// Unique value that identifies the device
  id: string;
}

// eslint-disable-next-line
interface ErrorResponseDto {
  errorCode: number;
  errorType: DeviceErrorType;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private nativeService = inject(NativeService);
  private http = inject(HttpClient);
  private translateService = inject(TranslateService);
  private globalDataRepo = inject(GlobalDataRepository);
  private logger = inject(LoggerService);
  private deviceDetectorService = inject(DeviceDetectorService);

  get deviceId(): string {
    return this.globalDataRepo.getDevice()?.id ?? '';
  }

  get deviceEnvironment(): DeviceEnvironment {
    if (!this.nativeService.isNative()) {
      return 'WEB';
    } else if (this.nativeService.isNativeIos()) {
      return 'IOS';
    } else {
      return 'GOOGLE_PLAY';
    }
  }

  get deviceType(): DeviceType {
    if (!this.nativeService.isNative()) {
      return this.deviceDetectorService.isDesktop() ? 'DESKTOP_WEB' : 'MOBILE_WEB';
    } else if (this.nativeService.isNativeIos()) {
      return 'IOS';
    } else if (this.nativeService.isNativeAndroid()) {
      return 'ANDROID';
    }
  }

  constructor() {
    const eventManager = inject(EventManager);

    eventManager.appLoaded$.subscribe(async () => this.evaluateIfWeNeedToRefresh());
  }

  async evaluateIfWeNeedToRefresh() {
    if (!environment.microservices?.device) {
      return;
    }
    if (this.globalDataRepo.getDevice() === null) {
      await this.doRefresh();
    }
  }

  async doRefresh() {
    const nativeIds = await this.getNativeIds();
    const request: FindDeviceRequestDto = {
      deviceIds: {
        idfa: nativeIds.adsId,
        clientId: nativeIds.guestId,
        appsFlyerId: nativeIds.appsFlyerId,
        firebaseAppInstanceId: nativeIds.firebaseId,
      },
      info: '',
    };

    const headers = {
      'Id-Device': this.deviceId,
      'Device-Type': this.deviceType,
      'Game-Name': 'slotcom',
      Environment: this.deviceEnvironment,
      'Game-Version': `${environment.VERSION}`,
      'Accept-Language': this.translateService.currentLang,
      Accept: 'application/vnd.device.v2+json',
    };

    try {
      const response = await lastValueFrom(
        this.http.post<DeviceDto>(`${environment.microservices.device}/refresh`, request, {
          headers: headers,
        })
      );
      this.globalDataRepo.updateGlobalData({ device: response });
    } catch (e) {
      this.logger.error(`Unable to create or update device: ${e}`);
    }
  }

  private async getNativeIds(): Promise<NativeIds> {
    if (this.nativeService.isNative()) {
      return await this.nativeService.getNativeIds();
    }

    // On Desktop, we store an uuid on the local-storage
    if (!this.globalDataRepo.getDesktopGuestId()) {
      this.globalDataRepo.updateGlobalData({
        desktopGuestId: uuidv4(),
      });
    }

    return {
      guestId: this.globalDataRepo.getDesktopGuestId(),
    };
  }

  public isMobileOrTablet(): boolean {
    return (
      this.nativeService.isNative() || this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet()
    );
  }
}
